import React, { useState, useRef, useEffect } from 'react';
import './Testimonials.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

const STATS = [
  { value: '36+', label: 'Years of Impact', icon: 'fa-calendar-alt' },
  { value: '500+', label: 'Students Touched', icon: 'fa-users' },
  { value: '50+', label: 'Active Volunteers', icon: 'fa-handshake' }
];

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Testimonials Component ---

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  // Fetch testimonials from database
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.fetchTestimonials();
        console.log('📋 Testimonials response:', result);
        
        if (result?.status === 'success' && result?.data?.length > 0) {
          // Filter only active testimonials
          const activeTestimonials = result.data.filter(t => t.status === 'active');
          setTestimonials(activeTestimonials);
          console.log(`✅ Loaded ${activeTestimonials.length} active testimonials`);
        } else {
          setError('No testimonials found');
          setTestimonials([]);
        }
      } catch (err) {
        console.error('❌ Error fetching testimonials:', err);
        setError(err.message || 'Failed to load testimonials');
        setTestimonials([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (isPaused || testimonials.length === 0) return;
    timerRef.current = setInterval(nextTestimonial, 8000);
    return () => clearInterval(timerRef.current);
  }, [isPaused, testimonials.length]);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index) => {
    setActiveIndex(index);
  };

  // Helper function to parse image from JSON
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    try {
      const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (e) {
      return imageData;
    }
  };

  // Clean HTML from Summernote
  const cleanHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Fallback image if no image available
  const getInitials = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  return (
    <>
      <Header />
      <div className="testimonials-page devnar">
        <a className="skip-link" href="#main-testimonials">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-testimonials">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-testimonials">
            <Reveal>
              <span className="hero-badge">In Their Words</span>
              <h1>Testimonials</h1>
              <p className="hero-subtitle">
                In the words of our volunteers, well-wishers, and partners — 
                the impact of Devnar Foundation through their eyes.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-testimonials" className="wrap page-content-testimonials">

          {/* --- Stats --- */}
          {!loading && testimonials.length > 0 && (
            <section className="testimonials-stats">
              <div className="stats-grid">
                {STATS.map((stat, index) => (
                  <Reveal key={stat.label} className="stat-card" delay={index * 60}>
                    <div className="stat-icon">
                      <i className={`fas ${stat.icon}`}></i>
                    </div>
                    <span className="stat-number">{stat.value}</span>
                    <span className="stat-label">{stat.label}</span>
                  </Reveal>
                ))}
                <Reveal className="stat-card" delay={180}>
                  <div className="stat-icon">
                    <i className="fas fa-comment"></i>
                  </div>
                  <span className="stat-number">{testimonials.length}+</span>
                  <span className="stat-label">Testimonials</span>
                </Reveal>
              </div>
            </section>
          )}

          {/* --- Loading State --- */}
          {loading && (
            <section className="testimonials-loader">
              <div className="loader-spinner"></div>
              <p>Loading testimonials...</p>
            </section>
          )}

          {/* --- Error State --- */}
          {error && !loading && (
            <section className="testimonials-error">
              <i className="fas fa-exclamation-circle"></i>
              <p>{error}</p>
              <button 
                className="btn btn-secondary" 
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-redo"></i> Try Again
              </button>
            </section>
          )}

          {/* --- No Testimonials --- */}
          {!loading && !error && testimonials.length === 0 && (
            <section className="testimonials-empty">
              {/* <i className="fas fa-comment-slash"></i>
              <p>No testimonials available yet. Check back soon!</p> */}
            </section>
          )}

          {/* --- Featured Testimonial Carousel --- */}
          {!loading && testimonials.length > 0 && (
            <section className="featured-testimonial">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Featured</span>
                <h2>What Our Volunteers Say</h2>
                <div className="rule" />
              </Reveal>

              <div 
                className="testimonial-carousel"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <div className="carousel-container">
                  {testimonials.map((testimonial, index) => {
                    const imageUrl = getImageUrl(testimonial.image);
                    return (
                      <div 
                        key={testimonial.id}
                        className={`carousel-slide ${index === activeIndex ? 'active' : ''}`}
                      >
                        <div className="testimonial-card-large">
                          <div className="testimonial-quote-icon">
                            <i className="fas fa-quote-right"></i>
                          </div>
                          <div className="testimonial-content">
                            <p>{cleanHtml(testimonial.testimonial_en || testimonial.testimonial)}</p>
                          </div>
                          <div className="testimonial-author">
                            {imageUrl ? (
                              <img src={imageUrl} alt={testimonial.name_en || testimonial.name} loading="lazy" />
                            ) : (
                              <div className="testimonial-avatar-placeholder">
                                {getInitials(testimonial.name_en || testimonial.name)}
                              </div>
                            )}
                            <div>
                              <h4>{testimonial.name_en || testimonial.name}</h4>
                              {testimonial.designation_en && (
                                <span className="author-role">{cleanHtml(testimonial.designation_en)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button className="carousel-btn prev" onClick={prevTestimonial} aria-label="Previous">
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button className="carousel-btn next" onClick={nextTestimonial} aria-label="Next">
                  <i className="fas fa-chevron-right"></i>
                </button>

                <div className="carousel-dots">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      className={`dot ${index === activeIndex ? 'active' : ''}`}
                      onClick={() => goToTestimonial(index)}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* --- All Testimonials Grid --- */}
          {!loading && testimonials.length > 0 && (
            <section className="all-testimonials">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">More Stories</span>
                <h2>All Testimonials</h2>
                <div className="rule" />
              </Reveal>

              <div className="testimonials-grid">
                {testimonials.map((testimonial, index) => {
                  const imageUrl = getImageUrl(testimonial.image);
                  return (
                    <Reveal key={testimonial.id} className="testimonial-card" delay={(index % 5) * 80}>
                      <div className="testimonial-header">
                        {imageUrl ? (
                          <img src={imageUrl} alt={testimonial.name_en || testimonial.name} loading="lazy" />
                        ) : (
                          <div className="testimonial-avatar-placeholder small">
                            {getInitials(testimonial.name_en || testimonial.name)}
                          </div>
                        )}
                        <div>
                          <h4>{testimonial.name_en || testimonial.name}</h4>
                          {testimonial.designation_en && (
                            <span className="testimonial-role">{cleanHtml(testimonial.designation_en)}</span>
                          )}
                        </div>
                      </div>
                      <div className="testimonial-body">
                        <span className="quote-mark">
                          <i className="fas fa-quote-left"></i>
                        </span>
                        <p>{cleanHtml(testimonial.testimonial_en || testimonial.testimonial)}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </section>
          )}

          {/* --- Call to Action --- */}
          <section className="testimonial-cta">
            <Reveal className="cta-content" as="div">
              <span className="cta-eyebrow">Share Your Story</span>
              <h2>Have You Volunteered With Us?</h2>
              <p>
                We'd love to hear about your experience with Devnar Foundation. 
                Share your story and inspire others to join our mission.
              </p>
              <div className="cta-buttons">
                <a href="/volunteer" className="btn btn-primary">
                  <i className="fas fa-hands-helping"></i> Become a Volunteer
                </a>
                <a href="/contact" className="btn btn-outline">
                  <i className="fas fa-envelope"></i> Share Your Testimonial
                </a>
              </div>
            </Reveal>
          </section>

        </main>
      </div>
      <Footer />
    </>
  );
}